import { ReportLocationEnum } from 'src/enums/reportLocationEnum';
import { PortCall } from 'src/types/ViewModel/PortCall';
import { Rotation } from 'src/types/ViewModel/Rotation';
import { VoyageOption } from 'src/types/ViewModel/VoyageOption';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { DEFAULT_FIRST_VOYAGE_NUMBER } from 'src/utils/stowage/manifest/cargoManifestUtils';

/**
 * Generates voyage options using stowage integration logic when available,
 * falls back to events-based logic for non-stowage vessels.
 * 
 * For STOWAGE module (isStowageModule=true):
 * - Only uses stowagePlan.rotation (no merging with updatedRotation)
 * - Voyages before cutoff get status 'N/A' (no label shown)
 * 
 * For EVENTS/CONSUMABLES modules (isStowageModule=false):
 * - When stowage plan is available, merges voyages from BOTH sources
 * - Stowage plan rotation (has latest/future voyages with stowage data)
 * - UpdatedRotation (has full rotation including earlier voyages back to 202401)
 */
export const generateUnifiedVoyageOptionsList = (
    hasStowageIntegration: boolean,
    updatedRotation: Rotation | null,
    currentVoyage: string,
    stowagePlan: StowagePlan | null = null,
    vessel: Vessel | null = null,
    isStowageModule: boolean = false
): VoyageOption[] => {
    const hasStowagePlanRotation = stowagePlan?.rotation && stowagePlan.rotation.length > 0;
    const shouldUseStowageLogic = hasStowageIntegration || hasStowagePlanRotation;

    if (isStowageModule && stowagePlan) {
        // Stowage module: Only use stowage plan rotation (no merging with updatedRotation)
        return generateStowageVoyageOptions(stowagePlan, vessel);
    } else if (shouldUseStowageLogic) {
        // Events/Consumables with stowage data: Merge both sources
        return generateMergedVoyageOptions(stowagePlan, vessel, updatedRotation, currentVoyage);
    } else {
        // Events/Consumables without stowage data: Use updatedRotation only
        return generateEventsVoyageOptions(updatedRotation, currentVoyage);
    }
};

/**
 * Generates voyage options using stowage integration logic (from StowagePage)
 */
const generateStowageVoyageOptions = (stowagePlan: StowagePlan, vessel: Vessel | null): VoyageOption[] => {
    if (!stowagePlan?.rotation?.length) {
        return [];
    }

    const voyageNumberList = stowagePlan.rotation.reduce((result, portCall) => {
        // Add port call to primary voyage number group
        if (portCall.voyageNumber) {
            (result[portCall.voyageNumber] = result[portCall.voyageNumber] || []).push(portCall);
        }

        // Also add to turnaround voyage number group if it exists
        if (portCall.turnaroundVoyageNumber && portCall.turnaroundVoyageNumber !== portCall.voyageNumber) {
            (result[portCall.turnaroundVoyageNumber] = result[portCall.turnaroundVoyageNumber] || []).push(portCall);
        }

        return result;
    }, {} as Record<string, RotationPortCall[]>);

    const cutOffVoyage = vessel?.firstVoyageOrcaWebStowageImosSync ?? DEFAULT_FIRST_VOYAGE_NUMBER;

    const voyageOptionList: VoyageOption[] = Object.entries(voyageNumberList)
        // remove voyages < DEFAULT_FIRST_VOYAGE_NUMBER
        .filter(([voyageNumber]) => Number(voyageNumber) >= DEFAULT_FIRST_VOYAGE_NUMBER)
        .map(([voyageNumber, portCallArray]) => {
            const lastPortCall = portCallArray[portCallArray.length - 1];
            const startPort = portCallArray[0]?.portDisplayName || '';
            const endPort = lastPortCall?.portDisplayName || '';

            let status = 'completed'; // Default status (if IMOS status is completed or closed)

            // Find the most relevant voyage status from all port calls in this voyage
            // Priority: Commenced > Scheduled > Completed/Closed
            const commencedPortCall = portCallArray.find((pc) => pc.voyageStatus === 'Commenced' && pc.voyageNumber === voyageNumber);
            const scheduledPortCall = portCallArray.find((pc) => pc.voyageStatus === 'Scheduled' && pc.voyageNumber === voyageNumber);

            if (Number(voyageNumber) < cutOffVoyage) {
                status = 'N/A';
            } else if (commencedPortCall) {
                status = 'commenced';
            } else if (scheduledPortCall) {
                status = 'scheduled';
            }

            return new VoyageOption(voyageNumber, startPort, endPort, status);
        });

    return sortVoyageOptions(voyageOptionList);
};


/**
 * Generates voyage options by merging BOTH sources:
 * - Stowage plan rotation: Contains voyages with stowage data (typically future/recent voyages)
 * - UpdatedRotation: Contains full rotation from API (includes earlier voyages back to 202401)
 * Used by EVENTS/CONSUMABLES modules to show all available voyages.
 */
const generateMergedVoyageOptions = (
    stowagePlan: StowagePlan | null,
    vessel: Vessel | null,
    updatedRotation: Rotation | null = null,
    currentVoyage: string = ''
): VoyageOption[] => {
    const cutOffVoyage = vessel?.firstVoyageOrcaWebStowageImosSync ?? DEFAULT_FIRST_VOYAGE_NUMBER;
    const voyageOptionList: VoyageOption[] = [];
    const processedVoyages = new Set<string>();

    // STEP 1: Process voyages from stowage plan rotation (has latest/future voyages)
    if (stowagePlan?.rotation?.length) {
        const voyageNumberList = stowagePlan.rotation.reduce((result, portCall) => {
            if (portCall.voyageNumber) {
                (result[portCall.voyageNumber] = result[portCall.voyageNumber] || []).push(portCall);
            }
            if (portCall.turnaroundVoyageNumber && portCall.turnaroundVoyageNumber !== portCall.voyageNumber) {
                (result[portCall.turnaroundVoyageNumber] = result[portCall.turnaroundVoyageNumber] || []).push(portCall);
            }
            return result;
        }, {} as Record<string, RotationPortCall[]>);

        Object.entries(voyageNumberList)
            .filter(([voyageNumber]) => Number(voyageNumber) >= DEFAULT_FIRST_VOYAGE_NUMBER)
            .forEach(([voyageNumber, portCallArray]) => {
                const lastPortCall = portCallArray[portCallArray.length - 1];
                const startPort = portCallArray[0]?.portDisplayName || '';
                const endPort = lastPortCall?.portDisplayName || '';

                let status = 'completed';
                const commencedPortCall = portCallArray.find((pc) => pc.voyageStatus === 'Commenced' && pc.voyageNumber === voyageNumber);
                const scheduledPortCall = portCallArray.find((pc) => pc.voyageStatus === 'Scheduled' && pc.voyageNumber === voyageNumber);

                if (Number(voyageNumber) < cutOffVoyage) {
                    status = 'N/A';
                } else if (commencedPortCall) {
                    status = 'commenced';
                } else if (scheduledPortCall) {
                    status = 'scheduled';
                }

                voyageOptionList.push(new VoyageOption(voyageNumber, startPort, endPort, status));
                processedVoyages.add(voyageNumber);
            });
    }

    // STEP 2: Add voyages from updatedRotation that are NOT in the stowage plan
    if (updatedRotation?.portCallArray?.length) {
        const portCalls = updatedRotation.portCallArray.filter((pc) => pc.reportLocation === ReportLocationEnum.Port);
        const voyageToPortCallsMap: Record<string, typeof portCalls> = {};

        portCalls.forEach((portCall) => {
            if (portCall.voyageNumber1 && !processedVoyages.has(portCall.voyageNumber1)) {
                if (!voyageToPortCallsMap[portCall.voyageNumber1]) {
                    voyageToPortCallsMap[portCall.voyageNumber1] = [];
                }
                voyageToPortCallsMap[portCall.voyageNumber1].push(portCall);
            }
            if (portCall.voyageNumber2 && !processedVoyages.has(portCall.voyageNumber2)) {
                if (!voyageToPortCallsMap[portCall.voyageNumber2]) {
                    voyageToPortCallsMap[portCall.voyageNumber2] = [];
                }
                voyageToPortCallsMap[portCall.voyageNumber2].push(portCall);
            }
        });

        Object.entries(voyageToPortCallsMap).forEach(([voyageNumber, voyagePortCalls]) => {
            if (Number(voyageNumber) < DEFAULT_FIRST_VOYAGE_NUMBER) return;
            if (voyagePortCalls.length === 0) return;

            const startPort = voyagePortCalls[0]?.name || '';
            const endPort = voyagePortCalls[voyagePortCalls.length - 1]?.name || '';

            let status = 'completed';
            if (Number(voyageNumber) < cutOffVoyage) {
                status = 'N/A';
            } else if (Number(voyageNumber) > Number(currentVoyage)) {
                status = 'scheduled';
            } else if (voyageNumber === currentVoyage) {
                status = 'commenced';
            }

            voyageOptionList.push(new VoyageOption(voyageNumber, startPort, endPort, status));
            processedVoyages.add(voyageNumber);
        });
    }

    return sortVoyageOptions(voyageOptionList);
};

/**
 * Generates voyage options using events-based logic (from ReportEvents)
 */
const generateEventsVoyageOptions = (updatedRotation: Rotation | null, currentVoyage: string): VoyageOption[] => {
    if (!updatedRotation?.portCallArray?.length) {
        return [];
    }

    // Only process port calls in ports (not at sea)
    const portCalls = updatedRotation.portCallArray.filter((portCall) => portCall.reportLocation === ReportLocationEnum.Port);

    // Build the voyage number mapping
    const voyageToPortCallsMap = buildVoyageToPortCallsMap(portCalls);

    // Convert map to voyage options array
    const voyageOptions: VoyageOption[] = Object.entries(voyageToPortCallsMap)
        .map(([voyageNumber, portCalls]) => createEventsVoyageOption(voyageNumber, portCalls, currentVoyage))
        .filter(Boolean) as VoyageOption[];

    return sortVoyageOptions(voyageOptions);
};

const buildVoyageToPortCallsMap = (portCalls: PortCall[]): Record<string, PortCall[]> => {
    const voyageToPortCallsMap: Record<string, PortCall[]> = {};

    portCalls.forEach((portCall) => {
        // Add port call to primary voyage number group
        if (portCall.voyageNumber1) {
            if (!voyageToPortCallsMap[portCall.voyageNumber1]) {
                voyageToPortCallsMap[portCall.voyageNumber1] = [];
            }
            voyageToPortCallsMap[portCall.voyageNumber1].push(portCall);
        }

        // Also add to secondary voyage number group if it exists
        if (portCall.voyageNumber2) {
            if (!voyageToPortCallsMap[portCall.voyageNumber2]) {
                voyageToPortCallsMap[portCall.voyageNumber2] = [];
            }
            voyageToPortCallsMap[portCall.voyageNumber2].push(portCall);
        }
    });

    return voyageToPortCallsMap;
};

const createEventsVoyageOption = (voyageNumber: string, portCalls: PortCall[], currentVoyage: string): VoyageOption | null => {
    try {
        if (!portCalls.length) return null;

        const firstPortCall = portCalls[0];
        const lastPortCall = portCalls[portCalls.length - 1];

        let voyageStatus = 'completed'; // Default status

        if (Number(voyageNumber) === Number(currentVoyage)) {
            voyageStatus = 'commenced'; // Current voyage is ongoing
        } else if (Number(voyageNumber) > Number(currentVoyage)) {
            voyageStatus = 'scheduled'; // Voyage has planned port calls
        }

        // Create voyage option with first and last port names
        return new VoyageOption(voyageNumber, firstPortCall?.name || '', lastPortCall?.name || '', voyageStatus);
    } catch (error) {
        console.error(`Error creating voyage option for voyage ${voyageNumber}:`, error);
        return null;
    }
};

const sortVoyageOptions = (voyageOptions: VoyageOption[]): VoyageOption[] => {
    const storedSortOrder = localStorage.getItem('isSortedVoyageListDesc');
    let isDescending = false;

    if (storedSortOrder) {
        isDescending = storedSortOrder === 'true';
    } else {
        // Check both possible localStorage keys for compatibility
        const legacySortOrder = localStorage.getItem('isSortedVoyageListAsc');
        if (legacySortOrder) {
            isDescending = legacySortOrder === 'true';
        }
    }

    return [...voyageOptions].sort((a, b) => {
        const numA = Number(a.voyageNumber);
        const numB = Number(b.voyageNumber);

        if (isDescending) {
            return numB - numA; // Descending: higher numbers first
        } else {
            return numA - numB; // Ascending: lower numbers first
        }
    });
};
